import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import InputField from "./InputField";

// Define the types for fields and filter data
interface Field {
  name: string;
  label: string;
  type: "text" | "select" | "date" | "number" | "checkbox";
  options?: { value: string | number; label: string }[]; // For select type
  defaultValue?: any; // Default value for the field
}

interface FilterableFormProps {
  title: string;
  fields: Field[];
  // onFilterChange: (filters: Record<string, any>) => void; // Define the onSubmit type here
  // onReset?: () => void; // Optional reset callback
  onClose: () => void; // Optional close callback
}

const FilterableForm: React.FC<FilterableFormProps> = ({ title, fields, onClose }) => {
  const [filters, setFilters] = useState<Record<string, any>>({});
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize filters with default values
  useEffect(() => {
    const initialFilters: Record<string, any> = {};
    fields.forEach((field) => {
      const queryValue = searchParams.get(field.name) || field.defaultValue || "";
      // Handle checkbox values specifically
      if (field.type === "checkbox") {
        initialFilters[field.name] = queryValue === "true";
      } else {
        initialFilters[field.name] = queryValue;
      }
    });
    setFilters(initialFilters);
  }, [fields, searchParams]);
  

  const handleChange = (name: string, value: any) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
  
    const newParams = new URLSearchParams(searchParams.toString());
    Object.keys(filters).forEach((key) => {
      const value = filters[key];
      const field = fields.find((f) => f.name === key);
  
      if (field?.type === "checkbox" && value === false) {
        newParams.delete(key); // Exclude unchecked checkboxes
      } else if (value) {
        newParams.set(key, value.toString());
      } else {
        newParams.delete(key); // Remove if value is empty
      }
    });
  
    newParams.set("page", "1"); // Reset pagination
    setSearchParams(newParams);
    onClose();
  };
  

  const handleReset = () => {
    const newParams = new URLSearchParams(searchParams.toString());
    fields.forEach((field) => newParams.delete(field.name)); // Clear all filters
    newParams.set("page", "1");
    setSearchParams(newParams);

    const resetFilters: Record<string, any> = {};
    fields.forEach((field) => {
      resetFilters[field.name] = field.defaultValue ?? "";
    });
    setFilters(resetFilters);
    // onReset && onReset(); // Optional reset callback
    onClose(); 
  };

  return (
    <div className="p-6 bg-white shadow-lg border border-gray-200 w-full">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6">{title}</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Grid Layout for Fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {fields.map((field) => (
            <div key={field.name} className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">{field.label}</label>
              {field.type === "select" ? (
                <select
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={filters[field.name] || ""}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                >
                  <option value="">Select {field.label}</option>
                  {field.options?.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              ) : field.type === "checkbox" ? (
                <div className="flex items-center space-x-2">
                  <InputField
                    type="checkbox"
                    checked={!!filters[field.name]} // Convert to boolean
                    onChange={(e) => handleChange(field.name, e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600 rounded-md focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-600">{field.label}</span>
                </div>
              ) : (
                 <InputField
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  type={field.type}
                  value={filters[field.name] || ""}
                  onChange={(e) => handleChange(field.name, e.target.value)}
                />
              )}
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={handleReset}
            className="px-4 py-2 text-[#0CAF60] border border-[#0CAF60] hover:bg-slate-200 text-sm font-medium rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
          >
            Reset Filters
          </button>
          <button
            type="submit"
            className="px-4 py-2 text-white bg-[#0CAF60] hover:bg-[#098d4d] disabled:bg-[#0caf609a] text-sm font-medium rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Apply Filters
          </button>
        </div>
      </form>
    </div>
  );
};

export default FilterableForm;



const parseSearchParams = (params: URLSearchParams) => {
  const obj: { [key: string]: any } = {};
  params.forEach((value, key) => {
    // Assuming multiple values are joined by commas
    obj[key] = value.includes(',') ? value.split(',') : value;
  });
  return obj;
};

export { parseSearchParams };
